/**
 * measureConditions.js — ОТМЕТКА ОБ УСЛОВИЯХ ЗАМЕРА И ПРЕДУПРЕЖДЕНИЯ. [12.0.23]
 *
 * Правила и подписи — в measureConditionsCore.js (чистый модуль, под тестами).
 * Здесь то, что трогает окна и Cornerstone:
 *
 *   1. При НАЧАЛЕ замера (ANNOTATION_ADDED) — проверить окно, и если условия
 *      сомнительные (MIP-слэб, наклонённая серия «по телу») — предупредить
 *      сразу, пока врач ещё тянет линейку, а не после.
 *   2. При ЗАВЕРШЕНИИ замера (ANNOTATION_COMPLETED) — записать условия В САМ
 *      замер: metadata.условияИзмерения. Замеры сохраняются в базу JSON-ом
 *      целиком (studyToolsUI), поэтому отметка переживает перезапуск.
 *   3. Показать замеры с их условиями в списке «Аннотации» левой панели.
 *      Этот список до 12.0.23 не заполнял НИКТО: разметка была, а внутри
 *      всегда «Нет аннотаций».
 *
 * КАК УЗНАТЬ ОКНО ЗАМЕРА. ANNOTATION_ADDED в новых Cornerstone несёт
 * viewportId, ANNOTATION_COMPLETED — нет. Поэтому запоминаем окно, в котором
 * последним нажали кнопку мыши: замер рождается именно там.
 *
 * EXPORTS: initMeasureConditions, условияОкна, отрисоватьСписокЗамеров
 */

import { log } from '../utils/logger.js';
import { showToast } from '../utils/helpers.js';
import { getRenderingEngine, getMprAlignMode, getMprTiltDeg } from '../core/viewport.js';
import { getAnnotations, хранитсяЛиАннотация, removeAnnotation } from './toolManager.js';
import {
  РЕЖИМЫ_СЛЭБА, измерительныйЛи, оценитьРиск, подписьУсловий,
  ИМЕНА_ИНСТРУМЕНТОВ, значениеЗамера,
} from './measureConditionsCore.js';

/** Окно, где последним нажали кнопку мыши, и когда. */
let _последнееОкно = null;

/** Замеры, о которых уже предупредили: один раз на замер, не на каждый сдвиг. */
const _предупреждены = new Set();

let _связан = false;

/** Плоскость по нормали камеры: какая ось тела ближе всего к взгляду. */
function _плоскостьПоКамере(vp) {
  try {
    const n = vp.getCamera?.()?.viewPlaneNormal;
    if (!n || n.length !== 3) return '';
    const [x, y, z] = n.map(Math.abs);
    if (z >= x && z >= y) return 'аксиальный';
    if (x >= y) return 'сагиттальный';
    return 'корональный';
  } catch (e) { return ''; }
}

/**
 * Условия окна прямо сейчас.
 * @param {string} viewportId
 * @returns {object|null}
 */
export function условияОкна(viewportId) {
  try {
    const vp = getRenderingEngine()?.getViewport?.(viewportId);
    if (!vp) return null;

    const тип = String(vp.type || '').toLowerCase();
    if (тип === 'stack') {
      return { вид: 'нативный срез', плоскость: 'как снято', окно: viewportId, когда: Date.now() };
    }
    if (тип === 'volume3d') {
      return { вид: '3D', окно: viewportId, когда: Date.now() };
    }

    const слэб = Number(vp.getSlabThickness?.());
    const смешивание = Number(vp.getBlendMode?.());
    return {
      вид: 'реформат',
      плоскость: _плоскостьПоКамере(vp),
      выравнивание: getMprAlignMode() === 'body' ? 'по телу' : 'по съёмке',
      слэбМм: Number.isFinite(слэб) ? Math.round(слэб * 10) / 10 : null,
      режимСлэба: РЕЖИМЫ_СЛЭБА[смешивание] || 'обычный',
      наклонГрад: Math.round(getMprTiltDeg() * 10) / 10,
      окно: viewportId,
      когда: Date.now(),
    };
  } catch (e) {
    log.debug('[Условия замера] окно:', e.message);
    return null;
  }
}

/**
 * Окно замера, если врач рисует его ПРЯМО СЕЙЧАС.
 *
 * Свежесть нажатия важна не меньше самого окна. Замеры, восстановленные из
 * базы при открытии серии, тоже порождают события — и штамповать их текущими
 * условиями значило бы написать неправду: снимали их месяц назад и, быть
 * может, на MIP. Без свежего нажатия мыши отметка не ставится, и в списке
 * честно стоит «условия не записаны».
 *
 * @param {object} detail — событие Cornerstone
 * @param {number} свежестьМс — сколько назад могло быть нажатие
 */
function _окноЗамера(detail, свежестьМс) {
  const свежее = _последнееОкно && (Date.now() - _последнееОкно.когда) < свежестьМс;
  if (!свежее) return null;
  return detail?.viewportId || _последнееОкно.id;
}

/** Замер начинают в момент нажатия: предупреждать есть смысл сразу. */
const СВЕЖЕСТЬ_НАЧАЛА_МС = 5000;
/** Линейку могут тянуть долго, но не дольше полуминуты. */
const СВЕЖЕСТЬ_ЗАВЕРШЕНИЯ_МС = 30000;

function _предупредить(аннотация, условия) {
  const uid = аннотация?.annotationUID;
  if (!uid || _предупреждены.has(uid)) return;
  const оценка = оценитьРиск(условия);
  if (оценка.уровень !== 'осторожно') return;
  _предупреждены.add(uid);
  showToast(`Замер в сомнительных условиях: ${оценка.причины.join('; ')}`, 'warning', 9000);
}

// ─────────────────────────────────────────────────────────────────────────
// Список «Аннотации» в левой панели
// ─────────────────────────────────────────────────────────────────────────

const _экран = (т) => String(т ?? '').replace(/[&<>"']/g, (с) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[с]));

export function отрисоватьСписокЗамеров() {
  const список = document.getElementById('annotations-list');
  if (!список) return;

  let все = [];
  try { все = (getAnnotations?.() || []).filter((а) => хранитсяЛиАннотация(а?.metadata?.toolName)); }
  catch (e) { log.debug('[Условия замера] список:', e.message); }

  if (!все.length) {
    список.innerHTML = '<div class="мз-пусто">Нет аннотаций</div>';
    return;
  }

  список.innerHTML = все.map((а) => {
    const имя = а?.metadata?.toolName || '';
    const у = а?.metadata?.условияИзмерения;
    const замер = измерительныйЛи(имя);
    const оценка = замер ? оценитьРиск(у) : { уровень: 'норма', причины: [] };
    const значение = значениеЗамера(а);
    return `<div class="мз-строка${оценка.уровень === 'осторожно' ? ' мз-осторожно' : ''}">
      <div class="мз-верх">
        <span class="мз-имя">${_экран(ИМЕНА_ИНСТРУМЕНТОВ[имя] || имя)}</span>
        <span class="мз-значение">${_экран(значение)}</span>
        <button class="мз-убрать" data-uid="${_экран(а.annotationUID)}" title="Удалить замер" aria-label="Удалить замер">×</button>
      </div>
      ${замер ? `<div class="мз-условия">${_экран(подписьУсловий(у))}</div>` : ''}
      ${оценка.причины.map((п) => `<div class="мз-причина">${_экран(п)}</div>`).join('')}
    </div>`;
  }).join('');
}

function _перерисоватьПозже() {
  clearTimeout(_перерисоватьПозже.т);
  _перерисоватьПозже.т = setTimeout(отрисоватьСписокЗамеров, 150);
}

// ─────────────────────────────────────────────────────────────────────────

export function initMeasureConditions() {
  if (_связан) return true;
  const csTools = window.cornerstoneTools || window.MikhmelViewer?._csTools;
  const События = csTools?.Enums?.Events;
  if (!События) return false;          // вызывающий повторит, когда инструменты поднимутся
  _связан = true;
  const цель = csTools.eventTarget || document;

  // Окно, где родится замер: нажатие мыши внутри элемента окна Cornerstone
  document.getElementById('viewport-container')?.addEventListener('pointerdown', (e) => {
    const эл = e.target?.closest?.('[data-viewport-uid]');
    const id = эл?.getAttribute('data-viewport-uid');
    if (id) _последнееОкно = { id, когда: Date.now() };
  }, { capture: true, passive: true });

  if (События.ANNOTATION_ADDED) {
    цель.addEventListener(События.ANNOTATION_ADDED, (evt) => {
      _перерисоватьПозже();   // и восстановленные из базы должны появиться в списке
      const а = evt.detail?.annotation;
      if (!а || !измерительныйЛи(а.metadata?.toolName)) return;
      const окно = _окноЗамера(evt.detail, СВЕЖЕСТЬ_НАЧАЛА_МС);
      const у = окно ? условияОкна(окно) : null;
      if (у) _предупредить(а, у);
    });
  }

  цель.addEventListener(События.ANNOTATION_COMPLETED, (evt) => {
    const а = evt.detail?.annotation;
    if (а && измерительныйЛи(а.metadata?.toolName) && а.metadata && !а.metadata.условияИзмерения) {
      const окно = _окноЗамера(evt.detail, СВЕЖЕСТЬ_ЗАВЕРШЕНИЯ_МС);
      const у = окно ? условияОкна(окно) : null;
      if (у) {
        а.metadata.условияИзмерения = у;
        _предупредить(а, у);   // на случай, если ANNOTATION_ADDED в этой сборке нет
      }
    }
    _перерисоватьПозже();
  });

  for (const имя of ['ANNOTATION_MODIFIED', 'ANNOTATION_REMOVED']) {
    if (События[имя]) цель.addEventListener(События[имя], _перерисоватьПозже);
  }

  // Удаление из списка
  document.getElementById('annotations-list')?.addEventListener('click', (e) => {
    const кн = e.target?.closest?.('.мз-убрать');
    if (!кн) return;
    try {
      removeAnnotation(кн.dataset.uid);
      getRenderingEngine()?.render?.();
    } catch (err) { log.debug('[Условия замера] удаление:', err.message); }
    _перерисоватьПозже();
  });

  отрисоватьСписокЗамеров();
  log.info('[Условия замера] отметка о реформате, выравнивании, слэбе и наклоне включена');
  return true;
}
